/**
 * @summary
 */
import { useContext, useEffect } from 'react';
import { ErrorPriority } from 'types';
import { LogError } from 'utils/logging/LogError';
import { initializeSession, initSessionFormValues } from 'utils/sessionUtils';
import { logClickEvent } from 'app-requests/triadms-apis/formLinkClickThroughTracking';
import {
  FLOODLIGHT_EVENTS,
  getTriadPixel,
  trackFloodlightActivity,
  trackPageView,
  trackSecondsOnPage,
  trackUserInitialScroll,
  trackFloodlightCounter,
  trackBingPageView,
  trackExperimentViewed,
} from 'utils/trackingFunctions';
import { configureSessionForLogging, LogWarning } from 'utils/logging';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { QUERY_PARAMS, REGION_COOKIE_NAME } from 'consts';
import {
  appendAndEvalRawHtml,
  getSidCookieOrQueryParams,
  setCookie,
} from 'utils/analyticsHelpers';
import { getSearchEngineCookies } from 'utils/getSearchEngine';
import { getUserProfile } from 'app-requests/triadms-apis/getUserProfile';
import { getQueryParams } from 'hooks/custom/useQueryParams';
import {
  hasActiveExperiment,
  getActiveExperiment,
} from 'utils/ab-testing/getActiveTestingVariant';
import { trackUsersExperimentInfo } from 'app-requests/triadmsRequests';

export default function SessionInitializer({
  updateUserData,
  questionnaire = {},
  siteMeta: {
    siteName,
    schoolCode,
    googleTagManagerSiteId,
    floodlightActivityValues,
    isPersonalized,
    geoLocation,
    siteType,
  },
  configEnv,
  environmentVariables = {},
}) {
  const {
    taxonomyValues,
    sessionInfo: { hasScrolled },
    actions: { updateSessionInfo },
  } = useContext(GlobalContext);

  // this will get current user info, such as questionnaire progress
  function handleReturningUser() {
    return getUserProfile(taxonomyValues).then((response) => {
      const questionAnswers = response.profile;
      if (questionnaire.steps && questionAnswers.length) {
        const sessionValues = initSessionFormValues(
          questionAnswers,
          questionnaire
        );
        updateUserData({
          sessionFormValues: sessionValues.formValues,
          linkedSessionFormValues: sessionValues.linkedSessionFormValues,
        });
      }
    });
  }

  useEffect(() => {
    const query = getQueryParams();
    const searchEngineCookies = getSearchEngineCookies();
    configureSessionForLogging({
      geoLocation,
      isPersonalized,
      googleTagManagerSiteId,
      schoolCode,
      developmentEnvironment: configEnv,
      searchEngineCookies,
      environmentVariables,
      siteType,
    });

    if (!schoolCode) {
      LogWarning('SessionInitializer: No school code found');
      return;
    }

    trackSecondsOnPage();
    trackSecondsOnPage(10);

    if (isPersonalized) {
      setCookie(REGION_COOKIE_NAME, geoLocation.region);
    }

    // this will create user session if needed
    initializeSession(query, { schoolCode, taxonomyValues })
      .then(({ sessionId, pageViewId, floodLightActivityFilters }) => {
        handleReturningUser().catch(() => /* not fatal */ null);

        configureSessionForLogging({
          sessionId,
          floodLightActivityFilters,
          sid: getSidCookieOrQueryParams(),
        });
        updateSessionInfo({
          userSessionId: sessionId,
          pageViewId,
          floodLightActivityFilters,
        });

        if (query[QUERY_PARAMS.CLICK_THROUGH_LOCATION_PARAM]) {
          logClickEvent(
            query[QUERY_PARAMS.CLICK_THROUGH_LOCATION_PARAM],
            schoolCode
          );
        }

        // For Triad Conversion Tracking
        getTriadPixel({
          pubCodeGuid: query[QUERY_PARAMS.PUB_CODE_GUID] || '',
          type: 'landing',
        })
          .then((pixels) => appendAndEvalRawHtml(pixels))
          .catch(() => /* not fatal */ null);

        const didFireFloodlight = trackFloodlightActivity(
          FLOODLIGHT_EVENTS.PAGE_VIEW,
          floodlightActivityValues,
          floodLightActivityFilters
        );

        if (!didFireFloodlight) {
          trackFloodlightCounter({
            google: {
              activityTagString: 'offli0',
              groupTagString: 'testi0',
              advertiserId: '13497326',
            },
          });
          if (searchEngineCookies.msclkid) {
            trackBingPageView({
              microsoft: {
                uetTagId: '247002750', // Catch All Tag
              },
            });
          }
        }
      })
      .catch((error) => {
        LogError(`Session Error: ${error.message}`, {
          priority: ErrorPriority.TWO,
        });
      })
      .finally(() => {
        trackPageView({
          questionnaireVariant: questionnaire.variant,
          siteName,
          schoolCode,
          googleTagManagerSiteId,
        });

        if (hasActiveExperiment()) {
          trackUsersExperimentInfo();
          getActiveExperiment().forEach(([experiment, variant]) => {
            trackExperimentViewed(
              `${experiment.id}-${experiment.name}`,
              `${variant.id}-${variant.name}`
            );
          });
        }
      });
  }, []);

  // The logic to tell us if a user has scrolled
  useEffect(() => {
    const threshold = 100;
    let initialized = false;
    let currentYPosition;

    const onScroll = () => {
      if (initialized) {
        currentYPosition =
          currentYPosition >= 0 ? currentYPosition : window.scrollY;
        const scrollDepth = Math.abs(currentYPosition - window.scrollY);
        const hasScrolled_ = scrollDepth > threshold;
        updateSessionInfo({ hasScrolled: hasScrolled_ });
        hasScrolled_ && trackUserInitialScroll();
      }
      initialized = true;
    };

    !hasScrolled &&
      window.addEventListener('scroll', onScroll, { passive: true });
    return () =>
      !hasScrolled &&
      window.removeEventListener('scroll', onScroll, { passive: true });
  }, [hasScrolled]);

  // We may need to return our datalayer here for Google Tag Manager
  return null;
}

SessionInitializer.propTypes = {};
